import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Drawer,
  Form,
  Input,
  Menu,
  message,
  Modal,
  Row,
  Table,
  Tooltip,
} from "antd";
import {
  DeleteOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  FileAddOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import EllipsisDropdown from "components/shared-components/EllipsisDropdown";
import Flex from "components/shared-components/Flex";
import utils from "utils";
import { useTranslation } from "react-i18next";
import Loading from "../../../../components/shared-components/Loading";
import { formatPrice } from "../../../../functions/formatters";
import { CURRENCY } from "../../../../constants/DeliveryConstants";
import { isMobile } from "../../../../functions/browser";
import locale from "antd/es/date-picker/locale/nl_NL";
import styled from "styled-components";
import dayjs from "dayjs";
import { useComplaints } from "../../../../data/hooks/firestore/useComplaints";
import { useCustomers } from "../../../../data/hooks/firestore/useCustomers";
import { COMPLAINT_REASONS } from "../../../../constants/DepartmentConstants";
import GeneralComplaintModal from "./modals/GeneralComplaintModal";
import { deleteDoc, doc } from "firebase/firestore";
import { firestore } from "../../../../configs/FirebaseConfig";
import { COMPANY_COUNTRY_CODE } from "../../../../constants/CompanyConstants";
import { FIRESTORE_COMPLAINTS_TABLE, FIRESTORE_REGIONS_TABLE } from "../../../../constants/FirestoreConstant";

const { RangePicker } = DatePicker;
const { confirm } = Modal;

const Complaints = ({ companyFilter = "All", title = "Complaints" }) => {
  const { t } = useTranslation();
  const [maxDaysAgo, setMaxDaysAgo] = useState(30);
  const [customDateRange, setCustomDateRange] = useState(null);
  const { data: complaints, loading: complaintsLoading } = useComplaints(maxDaysAgo, customDateRange);
  const { customers, isLoading: customersLoading } = useCustomers();
  const [filteredComplaints, setFilteredComplaints] = useState([]);
  const [visible, setVisible] = useState(false);
  const [complaint, setComplaint] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [datePickerForm] = Form.useForm();
  const [dateRange, setDateRange] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [generalComplaintModalVisible, setGeneralComplaintModalVisible] = useState(false);

  useEffect(() => {
    if (!!complaints) {
      setIsLoading(false);
      applyFilters();
    }
  }, [complaints, companyFilter, customers, dateRange, searchText]);

  const applyFilters = () => {
    let filteredData = complaints || [];

    // Filter by company first
    if (companyFilter !== "All") {
      filteredData = filteredData.filter((complaint) => {
        // Use new companyCode field, fallback to checking customer for old complaints
        if (complaint.companyCode) {
          return complaint.companyCode === companyFilter;
        }
        // Fallback for old complaints without companyCode
        const customer = customers.find((c) => c.id === complaint.farmorderCustomerId);
        return customer?.company === companyFilter;
      });
    }

    // Filter by date range on createdAt
    if (dateRange && dateRange.length > 0) {
      const startTimestamp = dateRange[0].startOf("day").unix();
      const endTimestamp = dateRange[1].endOf("day").unix();
      filteredData = filteredData.filter((complaint) => {
        return complaint.createdAt >= startTimestamp && complaint.createdAt <= endTimestamp;
      });
    }

    // Apply search filter
    if (searchText) {
      filteredData = utils.wildCardSearch(filteredData, searchText);
    }
    setFilteredComplaints(filteredData);
  };

  const showDrawer = (row) => {
    setVisible(true);
    setComplaint(row);
  };

  const closeDrawer = () => {
    setVisible(false);
    setComplaint(null);
  };

  const handlePeriodChange = (period) => {
    setMaxDaysAgo(period);
    setCustomDateRange(null);
    setDateRange([]);
    datePickerForm.resetFields();
  };

  const handleCustomDateRange = (range) => {
    if (range && range[0] && range[1]) {
      setCustomDateRange([range[0].format("YYYY-MM-DD"), range[1].format("YYYY-MM-DD")]);
      setMaxDaysAgo(null);
    } else {
      setCustomDateRange(null);
      setMaxDaysAgo(30);
    }
  };

  const handleDeleteComplaint = (row) => {
    const hasProducts = row.products && row.products.length > 0;

    if (hasProducts) {
      message.warning(t("cannot_delete_complaint"));
      return;
    }

    confirm({
      title: t("confirm_delete_complaint"),
      icon: <ExclamationCircleOutlined />,
      content: t("delete_complaint_warning"),
      okText: t("delete"),
      okType: "danger",
      cancelText: t("cancel"),
      onOk: async () => {
        try {
          const complaintRef = doc(
            firestore,
            `${FIRESTORE_REGIONS_TABLE}/${COMPANY_COUNTRY_CODE}/${FIRESTORE_COMPLAINTS_TABLE}`,
            row.id,
          );

          await deleteDoc(complaintRef);

          if (complaint?.id === row.id) {
            closeDrawer();
          }

          message.success(t("notifications.complaint_deleted_successfully"));
        } catch (error) {
          console.error("Error deleting complaint:", error);
          message.error(t("notifications.error_deleting_complaint"));
        }
      },
    });
  };

  const dropdownMenu = (row) => {
    const hasProducts = row.products && row.products.length > 0;

    return (
      <Menu>
        <Menu.Item key="view" onClick={() => showDrawer(row)}>
          <Flex alignItems="center">
            <EyeOutlined />
            <span style={{ marginLeft: 4 }}>{t("details")}</span>
          </Flex>
        </Menu.Item>
        <Menu.Item key="delete" onClick={() => !hasProducts && handleDeleteComplaint(row)} disabled={hasProducts}>
          <Tooltip title={hasProducts ? t("cannot_delete_complaint") : ""}>
            <DeleteOutlined />
            <span style={{ marginLeft: 4 }}>{t("delete_complaint")}</span>
          </Tooltip>
        </Menu.Item>
      </Menu>
    );
  };

  const tableColumns = [
    {
      title: "",
      render: (_, elm) => (
        <div className="text-right">
          <EllipsisDropdown menu={dropdownMenu(elm)} />
        </div>
      ),
    },
    {
      title: t("created_date"),
      dataIndex: "createdAt",
      sorter: (a, b) => Number(a.createdAt ?? 0) - Number(b.createdAt ?? 0),
      render: (_, data) => (
        <button className={"text-button"} onClick={() => showDrawer(data)}>
          <span style={{ minWidth: "120px", display: "block" }}>
            {data.createdAt ? dayjs.unix(data.createdAt).format("D MMM YYYY HH:mm") : "-"}
          </span>
        </button>
      ),
      defaultSortOrder: "descend",
    },
    {
      title: t("complaint_id"),
      dataIndex: "id",
      render: (id) => <span style={{ minWidth: "100px", display: "block" }}>{id}</span>,
    },
    {
      title: t("company"),
      dataIndex: "company",
      sorter: (a, b) => utils.antdTableSorter(a, b, "company"),
    },
    {
      title: t("invoice_id"),
      dataIndex: "invoiceId",
      render: (invoiceId) => <span>{invoiceId || "-"}</span>,
    },
    {
      title: t("order_id"),
      dataIndex: "farmorderId",
      render: (farmorderId) => <span>{farmorderId || "-"}</span>,
    },
    {
      title: t("complaining_person"),
      render: (_, data) => <span>{data.products?.[0]?.complainingPerson || data.complainingPerson || "-"}</span>,
    },
    {
      title: t("products"),
      render: (_, data) => <span>{data.products?.length || 0}</span>,
    },
    {
      title: t("total_amount"),
      render: (_, data) => {
        const total = data.products?.reduce((sum, p) => sum + (p.totalAmount || 0), 0) || 0;
        return <span style={{ minWidth: "65px", display: "block" }}>{CURRENCY + formatPrice(total)}</span>;
      },
      sorter: (a, b) => {
        const totalA = a.products?.reduce((sum, p) => sum + (p.totalAmount || 0), 0) || 0;
        const totalB = b.products?.reduce((sum, p) => sum + (p.totalAmount || 0), 0) || 0;
        return totalA - totalB;
      },
    },
  ];

  const onSearch = (value) => {
    setSearchText(value);
  };

  const handleDateRange = (dateRange) => {
    setDateRange(dateRange);
  };

  return (
    <>
      <Row className={"mb-2"}>
        <Col xs={24} md={16} xl={15} xxl={14}>
          <h1>{t(title)}</h1>
        </Col>
        <Col xs={24}>
          <Card>
            <Row gutter={[16, 8]} style={{ justifyContent: "space-between", marginBottom: ".5rem" }}>
              <Input
                placeholder={t("search")}
                style={{ width: "fit-content", maxHeight: 40, marginTop: "1rem" }}
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => onSearch(e.target.value)}
                allowClear
              />
              <ButtonsWrapper>
                <Button type="primary" icon={<FileAddOutlined />} onClick={() => setGeneralComplaintModalVisible(true)}>
                  {!isMobile && t("new_complaint")}
                </Button>
                <Tooltip title={t("filter_on_created_date")}>
                  <Form form={datePickerForm}>
                    <Form.Item name="dateRange" style={{ marginBottom: 0 }}>
                      <RangePicker
                        onChange={(e) => handleDateRange(e)}
                        locale={locale}
                        placeholder={[t("start"), t("end")]}
                        format={"DD-MM-YYYY"}
                        style={{ width: "145px" }}
                      />
                    </Form.Item>
                  </Form>
                </Tooltip>
              </ButtonsWrapper>
            </Row>
            {isLoading || customersLoading ? (
              <Loading />
            ) : (
              <div className="table-responsive">
                <Table
                  showSorterTooltip={false}
                  columns={tableColumns}
                  dataSource={filteredComplaints}
                  rowKey="id"
                  scroll={{ x: 1300 }}
                  pagination={{ showSizeChanger: true }}
                />
              </div>
            )}
            {!!complaint && (
              <Drawer
                title={`${t("complaint")} | ${complaint.company}`}
                placement="right"
                onClose={closeDrawer}
                open={visible}
                width={isMobile ? window.innerWidth : 850}
                styles={{ body: { padding: isMobile ? 2 : 16 } }}
              >
                <Descriptions bordered title={t("complaint_details")} size="small" column={1}>
                  <Descriptions.Item label={t("complaint_id")}>{complaint.id}</Descriptions.Item>
                  <Descriptions.Item label={t("invoice_id")}>{complaint.invoiceId || "-"}</Descriptions.Item>
                  <Descriptions.Item label={t("order_id")}>{complaint.farmorderId || "-"}</Descriptions.Item>
                  <Descriptions.Item label={t("exact_invoice_id")}>{complaint.exactInvoiceId || "-"}</Descriptions.Item>
                  <Descriptions.Item label={t("created_at")}>
                    {complaint.createdAt ? dayjs.unix(complaint.createdAt).format("D MMM YYYY HH:mm") : "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("created_by")}>{complaint.createdBy || "-"}</Descriptions.Item>
                  <Descriptions.Item label={t("updated_at")}>
                    {complaint.updatedAt ? dayjs.unix(complaint.updatedAt).format("D MMM YYYY HH:mm") : "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("updated_by")}>{complaint.updatedBy || "-"}</Descriptions.Item>
                  <Descriptions.Item label={t("company")}>{complaint.company || "-"}</Descriptions.Item>
                  {complaint.reason && (
                    <Descriptions.Item label={t("complaint_reason")}>
                      {(() => {
                        const reason = COMPLAINT_REASONS.find((r) => r.id === complaint.reason);
                        return reason ? t(reason.labelKey) : complaint.reason;
                      })()}
                    </Descriptions.Item>
                  )}
                  {complaint.customReason && (
                    <Descriptions.Item label={t("custom_reason")}>{complaint.customReason}</Descriptions.Item>
                  )}
                  {complaint.complainingPerson && (
                    <Descriptions.Item label={t("complaining_person")}>{complaint.complainingPerson}</Descriptions.Item>
                  )}
                  {complaint.contactDetails && (
                    <Descriptions.Item label={t("contact_details")}>{complaint.contactDetails}</Descriptions.Item>
                  )}
                  {complaint.description && (
                    <Descriptions.Item label={t("general_description")}>{complaint.description}</Descriptions.Item>
                  )}
                </Descriptions>

                {/* Images Section */}
                {complaint.images && complaint.images.length > 0 && (
                  <div style={{ marginTop: 16, marginBottom: 16 }}>
                    <h4 style={{ marginBottom: 12 }}>{t("uploaded_photos")}</h4>
                    <div
                      style={{
                        display: "flex",
                        gap: "12px",
                        flexWrap: "wrap",
                        padding: "12px",
                        backgroundColor: "#fafafa",
                        borderRadius: "6px",
                        border: "1px solid #e8e8e8",
                      }}
                    >
                      {complaint.images.map((imageUrl, index) => (
                        <a
                          key={index}
                          href={imageUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: "block",
                            width: "120px",
                            height: "120px",
                            borderRadius: "4px",
                            overflow: "hidden",
                            border: "2px solid #d9d9d9",
                            transition: "all 0.3s",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = "#1890ff";
                            e.currentTarget.style.transform = "scale(1.05)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = "#d9d9d9";
                            e.currentTarget.style.transform = "scale(1)";
                          }}
                        >
                          <img
                            src={imageUrl}
                            alt={`Complaint ${index + 1}`}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Products Table */}
                {complaint.products && complaint.products.length > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <h4 style={{ marginBottom: 12 }}>{t("products")}</h4>
                    <div className="table-responsive">
                      <Table
                        showSorterTooltip={false}
                        rowKey={(_, idx) => String(idx)}
                        pagination={false}
                        dataSource={complaint.products}
                        columns={[
                          {
                            title: t("product"),
                            render: (_, rec) =>
                              rec.productName ||
                              rec.product?.productName ||
                              rec.productId ||
                              rec.product?.productId ||
                              "-",
                          },
                          {
                            title: t("quantity"),
                            dataIndex: "quantity",
                            render: (val) => val || "-",
                          },
                          {
                            title: t("price"),
                            render: (_, rec) => (
                              <span>
                                {CURRENCY + formatPrice(parseFloat(rec.productPrice || rec.product?.productPrice || 0))}
                              </span>
                            ),
                          },
                          {
                            title: t("total_amount"),
                            render: (_, rec) => <span>{CURRENCY + formatPrice(rec.totalAmount || 0)}</span>,
                          },
                          {
                            title: t("complaint_reason"),
                            render: (_, rec) => {
                              const reason = COMPLAINT_REASONS.find((r) => r.id === rec.reason);
                              return reason ? t(reason.labelKey) : rec.reason;
                            },
                          },
                          {
                            title: t("custom_reason"),
                            dataIndex: "customReason",
                            render: (val) => val || "-",
                          },
                          {
                            title: t("complaining_person"),
                            dataIndex: "complainingPerson",
                            render: (val) => val || "-",
                          },
                        ]}
                      />
                    </div>
                  </div>
                )}
              </Drawer>
            )}
          </Card>
        </Col>
      </Row>
      <ButtonsWrapper style={{ justifyContent: "start", paddingBottom: "2rem" }}>
        <Button onClick={() => handlePeriodChange(30)} type={maxDaysAgo === 30 ? "primary" : "default"}>
          {t("last_30_days")}
        </Button>
        <Button onClick={() => handlePeriodChange(90)} type={maxDaysAgo === 90 ? "primary" : "default"}>
          {t("last_90_days")}
        </Button>
        <RangePicker onChange={handleCustomDateRange} placeholder={[t("start_date"), t("end_date")]} />
      </ButtonsWrapper>
      <GeneralComplaintModal
        visible={generalComplaintModalVisible}
        onClose={() => setGeneralComplaintModalVisible(false)}
        onSuccess={() => {
          setGeneralComplaintModalVisible(false);
        }}
        companyFilter={companyFilter}
      />
    </>
  );
};

const ButtonsWrapper = styled.div`
  display: flex;
  justify-content: end;
  margin-top: 1rem;
  flex-wrap: wrap;
  gap: 12px;

  @media (max-width: 576px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export default Complaints;
